import axios from 'axios';
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../Components/Redux/store';
import { setAuth } from '../Components/Redux/authSlice';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaClipboardList, FaCalendarAlt, FaCheckCircle, FaArrowLeft } from 'react-icons/fa';

interface AddExamProps {
  onSuccess: () => void;
}



const AddExam: React.FC<AddExamProps> = ({ onSuccess = () => { } }) => {
  const [examName, setExamName] = useState<string>('');
  const [date, setDate] = useState<string>('');
  const [branch, setBranch] = useState<number | null>(null);
  const [examType, setExamType] = useState<string>('SINGLE');
  const [catPercentage, setCatPercentage] = useState<number>(30);
  const [loading, setLoading] = useState<boolean>(false);
  const [userRole, setUserRole] = useState<string>('');
  const auth = useSelector((state: RootState) => state.auth.value);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const getFinalPercentage = () => 100 - catPercentage;

  const examTypeChoices = [
    { value: 'SINGLE', label: 'Single Exam (100%)', description: 'One comprehensive exam worth 100% of the grade' },
    { value: 'CAT_FINAL', label: 'CAT + Final', description: 'Continuous Assessment + Final Exam' },
  ];

  const getCookie = (name: string) => {
    const cookieName = name + "=";
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookieArray = decodedCookie.split(';');
    for (let i = 0; i < cookieArray.length; i++) {
      let cookie = cookieArray[i];
      while (cookie.charAt(0) === ' ') {
        cookie = cookie.substring(1);
      }
      if (cookie.indexOf(cookieName) === 0) {
        return cookie.substring(cookieName.length, cookie.length);
      }
    }
    return '';
  };

  useEffect(() => {
    const fetchUserData = async () => {
      setLoading(true);
      try {
        const token = getCookie('token');
        if (!token) {
          dispatch(setAuth(false));
          toast.error('No authentication token found. Please log in.');
          return;
        }
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const { data } = await axios.get('/users/user');
        setUserRole(data.user.role);
        dispatch(setAuth(true));
      } catch (error) {
        console.error('Error fetching user data:', error);
        dispatch(setAuth(false));
        toast.error('Failed to fetch user data');
      } finally {
        setLoading(false);
      }
    };
    fetchUserData();
  }, [dispatch]);

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!examName || !date || !examType) {
      toast.error('Fadlan buuxi dhammaan meelaha loo baahan yahay');
      return;
    }

    if (examName.length > 100) {
      toast.error('Magaca imtixaanku waa inuu ka yaraadaa 100 xaraf');
      return;
    }

    if (examType === 'CAT_FINAL' && (catPercentage < 10 || catPercentage > 50)) {
      toast.error('Boqolkiiba CAT waa inuu u dhexeeyaa 10% iyo 50%');
      return;
    }

    setLoading(true);
    try {
      const token = getCookie('token');
      if (!token) {
        toast.error('Lama helin token aqoonsi. Fadlan soo gal.');
        setLoading(false);
        return;
      }

      const newExam = {
        exam_name: examName,
        date,
        branch: branch || null,
        exam_type: examType,
        cat_percentage: examType === 'CAT_FINAL' ? catPercentage : 30,
      };
      await axios.post('/exams/exams/', newExam, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      toast.success('Imtixaanka si guul leh ayaa loo abuuray! Waa laguu wareejinayaa...');
      setExamName('');
      setDate('');
      setBranch(null);
      setExamType('SINGLE');
      onSuccess();
      setTimeout(() => {
        navigate('/exams');
      }, 2000);
    } catch (error: any) {
      console.error('Error creating exam:', error.response ? error.response.data : error.message);
      toast.error(error.response?.data?.detail || 'Khalad ayaa dhacay markii la abuurayay imtixaanka');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
          <span className="visually-hidden">Soo dejinaya...</span>
        </div>
      </div>
    );
  }

  if (!auth) {
    return (
      <div className="container-fluid min-vh-100 d-flex justify-content-center align-items-center bg-light">
        <div className="text-center">
          <h4 className="text-muted mb-3">Ma aadan xaqiijin</h4>
          <a href="/login" className="btn btn-primary px-4 py-2">Aad Login</a>
        </div>
      </div>
    );
  }

  if (userRole !== 'admin' && userRole !== 'super_admin') {
    return (
      <div className="container-fluid min-vh-100 d-flex justify-content-center align-items-center bg-light">
        <div className="text-center">
          <h4 className="text-muted mb-3">Ma lihid awood aad ku aragto boggan</h4>
          <a href="/dashboard" className="btn btn-primary px-4 py-2">Aad Dashboard-ka</a>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-content">
      <ToastContainer position="top-right" autoClose={3000} theme="colored" />

      {/* Header Section */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-5">
        <div>
          <h2 className="fw-bold text-dark mb-1">
            <FaClipboardList className="me-2 text-primary" />
            Ku Dar Imtixaan Cusub
          </h2>
          <p className="text-muted mb-0">Samee imtixaan cusub oo ardayda loogu talagalay</p>
        </div>
        <button className="btn btn-outline-secondary mt-3 mt-md-0 d-flex align-items-center" onClick={() => navigate('/exams')}>
          <FaArrowLeft className="me-2" /> Ku laabo Imtixaanada
        </button>
      </div>

      <div className="row justify-content-center">
        <div className="col-12 col-lg-8 col-xl-6">
          <div className="card-modern overflow-hidden">
            <div className="px-5 py-4 border-bottom" style={{ background: 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)' }}>
              <h4 className="text-primary fw-bold mb-0">Macluumaadka Imtixaanka</h4>
            </div>
            <div className="card-body p-5">
              <form onSubmit={handleFormSubmit}>
                {/* Exam Name */}
                <div className="mb-4">
                  <label htmlFor="examName" className="form-label fw-bold text-secondary">
                    <FaClipboardList className="me-2 text-primary" />
                    Magaca Imtixaanka <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-lg bg-light border-0"
                    id="examName"
                    placeholder="tusaale, Imtixaanka Dhexe 2024"
                    value={examName}
                    onChange={(e) => setExamName(e.target.value)}
                    maxLength={100}
                    required
                  />
                  <div className="form-text">Ugu badnaan 100 xaraf</div>
                </div>

                {/* Date */}
                <div className="mb-4">
                  <label htmlFor="date" className="form-label fw-bold text-secondary">
                    <FaCalendarAlt className="me-2 text-primary" />
                    Taariikhda Imtixaanka <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className="form-control form-control-lg bg-light border-0"
                    id="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    required
                  />
                </div>

                {/* Exam Type */}
                <div className="mb-4">
                  <label htmlFor="examType" className="form-label fw-bold text-secondary">
                    <i className="bi bi-diagram-3 me-2 text-primary"></i>
                    Nooca Imtixaanka <span className="text-danger">*</span>
                  </label>
                  <div className="row g-3">
                    {examTypeChoices.map((option) => (
                      <div key={option.value} className="col-md-6">
                        <div
                          className={`p-3 rounded border-2 cursor-pointer ${examType === option.value ? 'border-primary bg-primary bg-opacity-10' : 'border bg-light'}`}
                          style={{ cursor: 'pointer', transition: 'all 0.3s ease' }}
                          onClick={() => setExamType(option.value)}
                        >
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="examType"
                              id={option.value}
                              value={option.value}
                              checked={examType === option.value}
                              onChange={(e) => setExamType(e.target.value)}
                            />
                            <label className="form-check-label fw-bold" htmlFor={option.value}>
                              {option.value === 'SINGLE' ? 'Imtixaan Kaliya (100%)' : 'CAT + Final'}
                            </label>
                          </div>
                          <small className="text-muted d-block mt-2">
                            {option.value === 'SINGLE' ? 'Hal imtixaan oo dhameystiran (100%)' : 'Imtixaan Joogto ah + Imtixaan Kama Dambeys ah'}
                          </small>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* CAT Percentage - Only show for CAT_FINAL type */}
                {examType === 'CAT_FINAL' && (
                  <div className="mb-4">
                    <label className="form-label fw-bold text-secondary">
                      <i className="bi bi-sliders me-2 text-primary"></i>
                      Habeynta Qeybinta CAT/Final
                    </label>
                    <div className="card border-2 border-primary p-4">
                      <div className="row align-items-center mb-3">
                        <div className="col-md-6">
                          <label htmlFor="catPercentage" className="form-label small fw-semibold">
                            Boqolkiiba CAT: <span className="text-primary">{catPercentage}%</span>
                          </label>
                          <input
                            type="range"
                            className="form-range"
                            id="catPercentage"
                            min="10"
                            max="50"
                            step="5"
                            value={catPercentage}
                            onChange={(e) => setCatPercentage(parseInt(e.target.value))}
                          />
                          <div className="d-flex justify-content-between small text-muted">
                            <span>10%</span>
                            <span>50%</span>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="d-flex gap-3">
                            <div className="flex-fill text-center p-3 bg-primary bg-opacity-10 rounded">
                              <div className="fw-bold text-primary">CAT</div>
                              <div className="h4 mb-0">{catPercentage}%</div>
                            </div>
                            <div className="flex-fill text-center p-3 bg-success bg-opacity-10 rounded">
                              <div className="fw-bold text-success">Final</div>
                              <div className="h4 mb-0">{getFinalPercentage()}%</div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="alert alert-info mb-0 small">
                        <i className="bi bi-info-circle me-2"></i>
                        Macalimiinta waxay CAT-ga ka gelin doonaan {catPercentage} iyo Final-ka {getFinalPercentage()}
                      </div>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <div className="mt-5 border-top pt-4">
                  <button
                    type="submit"
                    className="btn btn-primary btn-lg w-100 shadow-lg"
                    disabled={loading}
                    style={{ borderRadius: '12px', padding: '15px' }}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Abuuraya Imtixaanka...
                      </>
                    ) : (
                      <>
                        <FaCheckCircle className="me-2" />
                        Abuur Imtixaan
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddExam;